import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { getCartItemTotalPrice, toGtmCartItems, useBookingFlowStore } from "~/stores/booking-flow-store";
import { GTMEvent } from "../../components/gtmProvider/GTMContext";
import useGTM from "../../components/gtmProvider/useGTM";
import PrimaryActionButton from "~/components/new/PrimaryActionButton";
import BackButton from "~/components/new/BackButton";
import { trpc } from "~/api/trpc";
import { ChevronLeftIcon, ChevronRightIcon, SunIcon, CloudIcon, MoonIcon, Loader2, Check, CheckCircle2, CheckCircle, Trash2, Eye, Calendar, ArrowRight, Plus } from "lucide-react";
import { keepPreviousData } from "@tanstack/react-query";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, addMonths, subMonths, addDays, startOfDay } from "date-fns";
import CartDrawer from "~/components/new/CartDrawer";
import { formatPractitionerName, type PastPractitionerOption, PastPractitionerPickerRow } from "~/components/new/PastPractitionerPicker";
import { useCustomerAuthStore } from "~/stores/customer-auth-store";
import { getDateLocaleByLocale, Language, useLanguageStore } from "~/stores/language-store";
import KaliaLogo from "~/components/new/KaliaLogo";
import { Input } from "~/components/ui/input";
import { toast } from "sonner"
import posthog from "posthog-js";
import toFixedNumber from "~/helper/common/toFixedNumber";
import LocalDateTime from "~/lib/localdatetime";
import { isStoreClosedOnDate, isSundayDate } from "~/lib/store-closed-days";
import TopProgressBar from "~/components/new/TopProgressBar";
import { Separator } from "~/components/ui/separator";

/** Same pattern as the former waitlist drawer email validation */
const WAITLIST_EMAIL_REGEX = /^[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}$/;

const FixedBottomBar = () => {
    const { selectedTimeslot } = useBookingFlowStore(
        useShallow((state) => ({
            selectedTimeslot: state.selectedTimeslot,
        }))
    );

    const { language } = useLanguageStore();

    const { data: cmsConfigData } = trpc.publicBookingFunnel.getCalendarSelectionConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const [isCartOpen, setIsCartOpen] = useState(false);

    const navigate = useNavigate();

    const handleClick = () => {
        if (!selectedTimeslot) {
            return;
        }
        navigate("/enter-details");
    }

    const continueButtonText = language === Language.GERMAN
        ? (cmsConfig?.continueButtonTextDe ?? 'WEITER')
        : (cmsConfig?.continueButtonTextEn ?? 'CONTINUE');

    return (
        <>
            <div className="fixed z-10 bottom-0 left-0 w-full md:max-w-7xl md:left-1/2 md:-translate-x-1/2 flex flex-col gap-3 px-6 pt-4 pb-6 bg-light-burgundy">
                <PrimaryActionButton
                    onClick={handleClick}
                    label={continueButtonText.toUpperCase()}
                    disabled={!selectedTimeslot}
                    rightIcon={<ArrowRight />}
                />
            </div>

            <CartDrawer
                open={isCartOpen}
                onClose={() => setIsCartOpen(false)}
                disableRemoveCartItems
            />
        </>
    )
}

interface Timeslot {
    startTime: LocalDateTime;
    startTimeStr: string;
}

type TimeOfDay = 'morning' | 'afternoon' | 'evening';

const CalendarSelection = () => {
    const { selectedStore, cartItems, selectedTimeslot, setSelectedTimeslot, waitlistChecked, setWaitlistChecked, waitlistDate, setWaitlistDate, email, setEmail, deeplinkPractitionerId, preferredPractitionerId, setPreferredPractitionerId } = useBookingFlowStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
            cartItems: state.cartItems,
            selectedTimeslot: state.selectedTimeslot,
            setSelectedTimeslot: state.setSelectedTimeslot,
            waitlistChecked: state.waitlistChecked,
            setWaitlistChecked: state.setWaitlistChecked,
            waitlistDate: state.waitlistDate,
            setWaitlistDate: state.setWaitlistDate,
            email: state.email,
            setEmail: state.setEmail,
            deeplinkPractitionerId: state.deeplinkPractitionerId,
            preferredPractitionerId: state.preferredPractitionerId,
            setPreferredPractitionerId: state.setPreferredPractitionerId,
        }))
    );

    const { isAuthenticated } = useCustomerAuthStore();
    const isLoggedIn = isAuthenticated();

    const { sendEvent } = useGTM();
    const navigate = useNavigate();

    const { t, language } = useLanguageStore();

    const signUpForWaitlistMutation = trpc.publicAppointments.signUpForWaitlistV2.useMutation({
        onSuccess: () => {
            toast.success(t('waitlistSignupSuccess'));
        },
        onError: (error) => {
            toast.error(t('waitlistSignupError'));
            console.error('Waitlist signup error:', error);
        },
    });

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getCalendarSelectionConfig.useQuery(undefined, {
        staleTime: 60000, // 1 minute cache
    });
    const cmsConfig = cmsConfigData?.config;

    // Component state
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<LocalDateTime | null>(null);
    const [selectedTimeOfDay, setSelectedTimeOfDay] = useState<TimeOfDay>('morning');
    const [showMoreTimeslots, setShowMoreTimeslots] = useState(false);
    const [timeslotViewMode, setTimeslotViewMode] = useState<"day-view" | "month-view">('day-view');
    const [waitlistEmailError, setWaitlistEmailError] = useState('');

    const variantIds = useMemo(() => {
        return cartItems.flatMap((item) => [item.selectedVariantId, ...item.addOns.map(addon => addon.variantId)]);
    }, [cartItems]);

    const constrainedPractitionerId = deeplinkPractitionerId ?? (isLoggedIn ? preferredPractitionerId : null);
    const shouldFetchPastPractitioners = isLoggedIn && !deeplinkPractitionerId && !!selectedStore && variantIds.length > 0;

    const pastPractitionersQuery = trpc.publicCustomer.getPastPractitionersForBooking.useQuery(
        {
            storeId: selectedStore?.id ?? -1,
            variantIds,
        },
        {
            enabled: shouldFetchPastPractitioners,
            staleTime: 10 * 60 * 1000,
        },
    );

    const pastPractitioners = pastPractitionersQuery.data ?? [];
    const showPastPractitionerPicker = !deeplinkPractitionerId && pastPractitioners.length > 0;

    useEffect(() => {
        if (!preferredPractitionerId) {
            return;
        }
        if (deeplinkPractitionerId || (pastPractitionersQuery.isFetched && !pastPractitioners.some((practitioner: { id: number }) => practitioner.id === preferredPractitionerId))) {
            setPreferredPractitionerId(null);
        }
    }, [deeplinkPractitionerId, pastPractitioners, pastPractitionersQuery.isFetched, preferredPractitionerId, setPreferredPractitionerId]);

    const handlePractitionerChange = (practitioner: PastPractitionerOption | null) => {
        const branch = {
            id: selectedStore?.id,
            name: selectedStore?.name,
        };

        if (!practitioner) {
            const previousPractitioner = pastPractitioners.find((option) => option.id === preferredPractitionerId);
            setPreferredPractitionerId(null);
            setSelectedTimeslot(null);
            if (previousPractitioner) {
                sendEvent(GTMEvent.BOOKING_FUNNEL_PRACTITIONER_DESELECTED, {
                    practitioner_id: previousPractitioner.id,
                    practitioner_name: formatPractitionerName(previousPractitioner),
                    practitioner_type: previousPractitioner.type,
                    branch,
                    variant_ids: variantIds,
                });
            }
            return;
        }

        setPreferredPractitionerId(practitioner.id);
        setSelectedTimeslot(null);
        sendEvent(GTMEvent.BOOKING_FUNNEL_PRACTITIONER_SELECTED, {
            practitioner_id: practitioner.id,
            practitioner_name: formatPractitionerName(practitioner),
            practitioner_type: practitioner.type,
            branch,
            variant_ids: variantIds,
        });
    };

    const queryParams = useMemo(() => ({
        storeId: selectedStore?.id ?? -1,
        from: startOfMonth(currentMonth).toISOString(),
        to: (() => {
            const fromDate = new Date();
            const endOfMonthDate = endOfMonth(currentMonth);
            const minEndDate = addDays(fromDate, 14); // 14 days including fromDate
            return (isBefore(endOfMonthDate, minEndDate) ? minEndDate : endOfMonthDate).toISOString();
        })(),
        variantIds: variantIds,
        existingAppointmentId: undefined,
        ...(constrainedPractitionerId ? { practitionerId: constrainedPractitionerId } : {}),
    }), [selectedStore?.id, variantIds, currentMonth, constrainedPractitionerId]);

    const availabilitiesQuery = trpc.publicAppointments.getAvailabilities.useQuery(queryParams, {
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        enabled: !!selectedStore?.id && variantIds.length > 0,
        staleTime: 10 * 60 * 1000, // 10 minutes
    });

    const [hasTriedToSelectNextMonth, setHasTriedToSelectNextMonth] = useState<boolean>(false);
    useEffect(() => {
        if (!availabilitiesQuery?.data || hasTriedToSelectNextMonth) {
            return;
        }
        if (availabilitiesQuery.data.length === 0) {
            goToNextMonth();
            setHasTriedToSelectNextMonth(true);
        }
    }, [availabilitiesQuery?.data]);

    const previousConstrainedPractitionerIdRef = useRef(constrainedPractitionerId);
    useEffect(() => {
        if (previousConstrainedPractitionerIdRef.current === constrainedPractitionerId) {
            return;
        }
        previousConstrainedPractitionerIdRef.current = constrainedPractitionerId;
        setCurrentMonth(new Date());
        setHasTriedToSelectNextMonth(false);
    }, [constrainedPractitionerId]);

    // Process availabilities data
    const timeslotsByDate: Record<string, { timeslot: Timeslot, priorityCategory: number }[]> = useMemo(() => {
        if (!availabilitiesQuery.data) {
            return {};
        }

        const rawAvailabilitiesData: { start: LocalDateTime, priorityCategory: number }[] = availabilitiesQuery.data.map((timeslot) => ({
            start: LocalDateTime.deserialize(timeslot?.startTimeSerialized),
            priorityCategory: timeslot.priorityCategory,
        }));
        const dateMap: Record<string, { timeslot: Timeslot, priorityCategory: number }[]> = {};

        const currentMonthStr = format(currentMonth, "yyyy-MM");

        rawAvailabilitiesData.filter((val) => {
            if (!val) {
                return false;
            }
            const dateKey = val.start.getDisplayDate();
            const monthStr = format(new Date(dateKey), "yyyy-MM");
            if (monthStr < currentMonthStr) {
                return false;
            }

            return true;
        }).forEach(timeslotData => {
            const dateKey = timeslotData.start.getDisplayDate();

            if (!(dateKey in dateMap)) {
                dateMap[dateKey] = [];
            }

            const timeslot = {
                startTime: timeslotData.start,
                startTimeStr: timeslotData.start.format("HH:mm")
            }

            dateMap[dateKey].push({
                timeslot: timeslot,
                priorityCategory: timeslotData.priorityCategory
            });
        });

        // Sort timeslots for each date by start time (earliest first)
        Object.keys(dateMap).forEach(dateKey => {
            dateMap[dateKey].sort((a, b) => {
                const aTime = a.timeslot.startTime.toUTC().getTime();
                const bTime = b.timeslot.startTime.toUTC().getTime();
                return aTime - bTime;
            });
        });

        return dateMap;
    }, [availabilitiesQuery.data]);


    const dateScrollContainerRef = useRef<HTMLDivElement>(null);
    const scrollToSelectedDate = (date: LocalDateTime) => {
        // console.log("scrollToSelectedDate", date);
        // console.log("dateScrollContainerRef", dateScrollContainerRef.current);
        if (!dateScrollContainerRef.current) return;

        const container = dateScrollContainerRef.current;
        const dateKey = date.format('yyyy-MM-dd');
        const dateButton = container.querySelector(`[data-date="${dateKey}"]`) as HTMLElement;
        if (dateButton) {
            const containerRect = container.getBoundingClientRect();
            const buttonRect = dateButton.getBoundingClientRect();

            // Calculate the scroll position to center the button
            const containerCenter = containerRect.width / 2;
            const buttonCenter = buttonRect.left - containerRect.left + buttonRect.width / 2;
            const scrollOffset = buttonCenter - containerCenter;

            container.scrollTo({
                left: container.scrollLeft + scrollOffset,
                behavior: 'smooth'
            });
        }
    };
    const [hasInitialized, setHasInitialized] = useState<boolean>(false);
    useEffect(() => {
        if (hasInitialized || !timeslotsByDate || Object.keys(timeslotsByDate).length === 0) {
            return;
        }
        // Select the first available timeslot (if exists)
        // together with the first available time of day (if exists)
        if (Object.keys(timeslotsByDate).length > 0 && timeslotsByDate[Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b))[0]].length > 0) {
            const firstAvailableTimeslot = timeslotsByDate[Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b))[0]][0];
            const firstAvailableDate = firstAvailableTimeslot.timeslot.startTime;
            setSelectedDate(firstAvailableDate);

            // Figure out if it is morning, afternoon or evening
            const hour = firstAvailableTimeslot.timeslot.startTime.getHours();
            if (hour < 12) {
                setSelectedTimeOfDay('morning');
            } else if (hour >= 12 && hour < 16) {
                setSelectedTimeOfDay('afternoon');
            } else {
                setSelectedTimeOfDay('evening');
            }
            setHasInitialized(true);
            setTimeout(() => {
                scrollToSelectedDate(firstAvailableDate);
            }, 100);
        }
    }, [timeslotsByDate]);

    useEffect(() => {
        if (!selectedDate || Object.keys(timeslotsByDate).length === 0) {
            return;
        }

        const timeslotDateKey = selectedDate.format('yyyy-MM-dd');
        const timeslotsOnDate = timeslotsByDate[timeslotDateKey];
        if (!timeslotsOnDate || timeslotsOnDate.length === 0) {
            return;
        }

        const firstAvailableTimeslot = timeslotsOnDate[0];
        console.log(firstAvailableTimeslot.timeslot.startTime.getDisplayDateTime())

        // Figure out if it is morning, afternoon or evening
        const hour = firstAvailableTimeslot.timeslot.startTime.getHours();
        if (hour < 12) {
            setSelectedTimeOfDay('morning');
        } else if (hour >= 12 && hour < 16) {
            setSelectedTimeOfDay('afternoon');
        } else {
            setSelectedTimeOfDay('evening');
        }
        setHasInitialized(true);
    }, [selectedDate]);

    // Get days for current month
    const monthDays = useMemo(() => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const today = new Date();

        return eachDayOfInterval({ start, end })
            .filter(day => !isBefore(day, today) || isSameDay(day, today))
            .map(day => format(day, 'yyyy-MM-dd'));
    }, [currentMonth]);

    // Get appointments count for a specific date
    const getAppointmentsCount = (date: LocalDateTime, timeOfDay?: TimeOfDay) => {
        const dateKey = date.getDisplayDate();
        const timeslots = timeslotsByDate[dateKey] || [];
        if (timeOfDay) {
            return timeslots.filter(slot => {
                const hour = slot.timeslot.startTime.getHours();
                switch (timeOfDay) {
                    case 'morning':
                        return hour < 12;
                    case 'afternoon':
                        return hour >= 12 && hour < 16;
                    case 'evening':
                        return hour >= 16;
                }
            }).length;
        }
        return timeslots.length;
    };

    // Helper function to get "niceness" score for a timeslot
    const getTimeslotScore = (date: LocalDateTime) => {
        const minutes = date.getMinutes();
        let score = 0;

        // Only show timeslots divisible by 5 minutes
        if (minutes % 5 !== 0) return -1;

        // Prefer times with better minute intervals (higher score)
        if (minutes === 0) score += 100; // hh:00
        else if (minutes % 30 === 0) score += 80; // hh:30
        else if (minutes % 15 === 0) score += 60; // hh:15, hh:45
        else if (minutes % 10 === 0) score += 40; // hh:10, hh:20, hh:40, hh:50
        else if (minutes % 5 === 0) score += 20; // hh:05, hh:25, hh:35, hh:55

        return score;
    };

    // Select best timeslots prioritizing better categories, with even time distribution across already-selected slots
    const selectBestTimeslots = (timeslots: { timeslot: Timeslot; priorityCategory: number; }[], maxCount: number) => {
        if (timeslots.length <= maxCount) return timeslots.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());

        const byCategory = new Map<number, typeof timeslots>();
        for (const slot of timeslots) {
            if (!byCategory.has(slot.priorityCategory)) {
                byCategory.set(slot.priorityCategory, []);
            }
            byCategory.get(slot.priorityCategory)!.push(slot);
        }

        for (const slots of byCategory.values()) {
            slots.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
        }

        const selected: typeof timeslots = [];
        let remaining = maxCount;

        const sortedCategories = [...byCategory.keys()].sort((a, b) => a - b);
        for (const category of sortedCategories) {
            if (remaining <= 0) break;
            const categorySlots = byCategory.get(category)!;

            if (categorySlots.length <= remaining) {
                selected.push(...categorySlots);
                remaining -= categorySlots.length;
            } else {
                const candidates = [...categorySlots];
                for (let i = 0; i < remaining; i++) {
                    if (selected.length === 0) {
                        const idx = Math.floor(candidates.length / 2);
                        selected.push(candidates.splice(idx, 1)[0]);
                    } else {
                        let bestIdx = 0;
                        let bestMinDist = -1;
                        let bestGapSize = -1;
                        const selectedTimes = selected.map(s => s.timeslot.startTime.toUTC().getTime()).sort((a, b) => a - b);
                        for (let c = 0; c < candidates.length; c++) {
                            const candidateTime = candidates[c].timeslot.startTime.toUTC().getTime();
                            let minDist = Infinity;
                            for (const t of selectedTimes) {
                                const dist = Math.abs(candidateTime - t);
                                if (dist < minDist) minDist = dist;
                            }
                            let gapSize = 0;
                            if (candidateTime <= selectedTimes[0]) {
                                gapSize = selectedTimes[0] - candidateTime;
                            } else if (candidateTime >= selectedTimes[selectedTimes.length - 1]) {
                                gapSize = candidateTime - selectedTimes[selectedTimes.length - 1];
                            } else {
                                for (let j = 0; j < selectedTimes.length - 1; j++) {
                                    if (candidateTime >= selectedTimes[j] && candidateTime <= selectedTimes[j + 1]) {
                                        gapSize = selectedTimes[j + 1] - selectedTimes[j];
                                        break;
                                    }
                                }
                            }
                            if (minDist > bestMinDist || (minDist === bestMinDist && gapSize > bestGapSize)) {
                                bestMinDist = minDist;
                                bestGapSize = gapSize;
                                bestIdx = c;
                            }
                        }
                        selected.push(candidates.splice(bestIdx, 1)[0]);
                    }
                }
                remaining = 0;
            }
        }

        return selected.sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
    };

    // Get timeslots for selected date and time of day
    const getTimeslotsForSelectedDateTime = () => {
        if (!selectedDate) return [];

        const dateKey = selectedDate.format('yyyy-MM-dd');
        const dayTimeslots = timeslotsByDate[dateKey] || [];

        const filteredByTime = dayTimeslots.filter(slot => {
            const hour = slot.timeslot.startTime.getHours();
            switch (selectedTimeOfDay) {
                case 'morning':
                    return hour < 12;
                case 'afternoon':
                    return hour >= 12 && hour < 16;
                case 'evening':
                    return hour >= 16;
                default:
                    return false;
            }
        });

        // Return all timeslots (they'll be filtered by selectBestTimeslots when needed)
        return filteredByTime.filter(slot => getTimeslotScore(slot.timeslot.startTime) >= 0).sort((a, b) => a.timeslot.startTime.toUTC().getTime() - b.timeslot.startTime.toUTC().getTime());
    };

    const filteredTimeslots = getTimeslotsForSelectedDateTime();
    const displayedTimeslots = showMoreTimeslots ? filteredTimeslots : selectBestTimeslots(filteredTimeslots, 4);
    const hasMoreTimeslots = filteredTimeslots.length > 4;

    // Navigation handlers
    const goToPreviousMonth = () => {
        const previousMonth = subMonths(currentMonth, 1);
        const today = new Date();
        if (!isBefore(previousMonth, startOfMonth(today))) {
            setCurrentMonth(previousMonth);
        }
    };

    const goToNextMonth = () => {
        setCurrentMonth(addMonths(currentMonth, 1));
    };

    const canGoPrevious = !isBefore(subMonths(currentMonth, 1), startOfMonth(new Date()));

    // Time of day options - use CMS labels
    const timeOfDayOptions = [
        { id: 'morning' as TimeOfDay, label: language === Language.GERMAN ? (cmsConfig?.morningLabelDe ?? 'Vormittag') : (cmsConfig?.morningLabelEn ?? 'Morning'), icon: SunIcon, range: '8-12' },
        { id: 'afternoon' as TimeOfDay, label: language === Language.GERMAN ? (cmsConfig?.afternoonLabelDe ?? 'Nachmittag') : (cmsConfig?.afternoonLabelEn ?? 'Afternoon'), icon: CloudIcon, range: '12-16' },
        { id: 'evening' as TimeOfDay, label: language === Language.GERMAN ? (cmsConfig?.eveningLabelDe ?? 'Abend') : (cmsConfig?.eveningLabelEn ?? 'Evening'), icon: MoonIcon, range: '16-20' },
    ];

    // Reset show more when date or time of day changes
    useEffect(() => {
        setShowMoreTimeslots(false);
        setWaitlistChecked(false);
        setWaitlistDate(null);
        setSelectedTimeslot(null);
    }, [selectedDate, selectedTimeOfDay]);

    // Scroll to selected date when day-view is rendered and data is loaded
    useEffect(() => {
        if (
            timeslotViewMode === 'day-view' &&
            !availabilitiesQuery.isFetching &&
            selectedDate &&
            dateScrollContainerRef.current
        ) {
            // Small delay to ensure DOM is rendered
            const timeoutId = setTimeout(() => {
                scrollToSelectedDate(selectedDate);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
    }, [timeslotViewMode, availabilitiesQuery.isFetching, selectedDate]);

    useEffect(() => {
        setSelectedTimeOfDay("morning");
        setShowMoreTimeslots(false);
        setWaitlistChecked(false);
        setWaitlistDate(null);
        setSelectedDate(null);
    }, [currentMonth]);

    const [sentEvent, setSentEvent] = useState<boolean>(false);

    useEffect(() => {
        if (shouldFetchPastPractitioners && !pastPractitionersQuery.isFetched) {
            return;
        }

        if (Object.keys(timeslotsByDate).length > 0 && timeslotsByDate[Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b))[0]].length > 0) {
            // Get the first available date from the data
            const firstAvailableTimeslot = timeslotsByDate[Object.keys(timeslotsByDate).sort((a, b) => a.localeCompare(b))[0]][0];
            const firstAvailableDate = firstAvailableTimeslot.timeslot.startTime;

            // The query range extends at least 14 days into the future, so the first slot can lie in a later month.
            const firstAvailableMonthStr = firstAvailableDate.getDisplayDate().slice(0, 7);
            if (firstAvailableMonthStr > format(currentMonth, "yyyy-MM")) {
                const [year, month] = firstAvailableMonthStr.split('-').map(Number);
                setCurrentMonth(new Date(year, month - 1, 1));
                return;
            }

            console.log("setting selected date", firstAvailableDate.getDisplayDateTime());
            console.log("currentMonth", currentMonth);
            setSelectedDate(firstAvailableDate);

            if (!sentEvent) {
                const totalTimeslotsNext14Days = Object.values(timeslotsByDate).reduce((acc, curr) => acc + curr.length, 0);
                const totalTimeslotsNext7Days = (() => {
                    const today = new Date();
                    const next7Days = addDays(today, 7);
                    let count = 0;

                    for (const [dateKey, timeslots] of Object.entries(timeslotsByDate)) {
                        const date = new Date(dateKey);
                        if (date >= today && date <= next7Days) {
                            count += timeslots.length;
                        }
                    }

                    return count;
                })();
                sendEvent(GTMEvent.BOOKING_FUNNEL_APPOINTMENT_SELECTION, {
                    cart_items: toGtmCartItems(cartItems),
                    branch: {
                        id: selectedStore?.id,
                        name: selectedStore?.name,
                    },
                    user: {
                        id: null,
                        has_account: false,
                        email: null,
                        phone: null,
                    },
                    calendar_free_timeslots_next_14_days: totalTimeslotsNext14Days,
                    calendar_free_timeslots_next_7_days: totalTimeslotsNext7Days,
                    preferred_practitioner_shown: showPastPractitionerPicker,
                });
                setSentEvent(true);
            }
        }
    }, [timeslotsByDate]);

    useEffect(() => {
        window.scroll(0, 0);
    }, []);

    useEffect(() => {
        if (!selectedStore) {
            navigate("/");
        }
    }, [selectedStore]);

    const isWaitlistEmailValid = WAITLIST_EMAIL_REGEX.test(email.trim());

    const handleJoinWaitlist = async () => {
        if (selectedDate && isStoreClosedOnDate(selectedDate.getDisplayDate(), selectedStore?.federalState, getAppointmentsCount(selectedDate) > 0)) {
            return;
        }
        const trimmed = email.trim();
        if (!trimmed) {
            setWaitlistEmailError(t('emailAddressRequired'));
            return;
        }
        if (!WAITLIST_EMAIL_REGEX.test(trimmed)) {
            setWaitlistEmailError(t('emailAddressHelpMessage'));
            return;
        }
        setWaitlistEmailError('');

        const bookableItems = cartItems.map((item) => ({
            bookableItemId: item.bookableItemId,
            bookableItemType: item.bookableItemType,
            treatmentVariantId: item.selectedVariantId,
            addOns: item.addOns.map((addon) => ({
                addOnId: addon.addOnId,
                variantId: addon.variantId,
            })),
            bundleId: item.bundleId,
        }));

        const desiredTimeOfDay =
            selectedTimeOfDay === 'morning'
                ? 'MORNING'
                : selectedTimeOfDay === 'afternoon'
                  ? 'AFTERNOON'
                  : 'EVENING';

        try {
            await signUpForWaitlistMutation.mutateAsync({
                email: trimmed,
                storeId: (!selectedStore?.id || selectedStore.id === -1) ? null : selectedStore.id,
                bookableItems,
                ...(selectedDate ? { desiredDate: selectedDate.getDisplayDate() } : {}),
                desiredTimeOfDay,
            });

            posthog.identify(trimmed.toLowerCase(), {
                name: '',
                email: trimmed.toLowerCase(),
                phone: '',
            });

            sendEvent(GTMEvent.BOOKING_FUNNEL_SIGNUP_WAITLIST, {
                firstName: '',
                lastName: '',
                email: trimmed.toLowerCase(),
                phone: '',
                user: {
                    email: trimmed.toLowerCase(),
                    phone: '',
                    has_account: false,
                },
                branch: {
                    id: selectedStore?.id,
                    name: selectedStore?.name,
                },
                cart_items: toGtmCartItems(cartItems),
                total_net: toFixedNumber(cartItems.reduce((sum, item) => sum + getCartItemTotalPrice(item) / 1.19, 0)),
                total_gross: toFixedNumber(cartItems.reduce((sum, item) => sum + getCartItemTotalPrice(item), 0)),
                branchName: selectedStore?.name ?? 'Digital',
                variants: cartItems.map((item) => `${item.selectedVariantName}(${item.selectedVariantId})`).join(', '),
            });

            setWaitlistChecked(true);
            if (selectedDate) {
                setWaitlistDate(new Date(selectedDate.getDisplayDate()));
            }
        } catch (err) {
            console.error('Error submitting waitlist:', err);
        }
    };

    const handleScrollDaysRight = () => {
        if (!dateScrollContainerRef.current) return;
        const container = dateScrollContainerRef.current;
        const scrollAmount = window.innerWidth * 0.7;
        container.scrollTo({
            left: container.scrollLeft + scrollAmount,
            behavior: 'smooth'
        });
    }

    const handleScrollDaysLeft = () => {
        if (!dateScrollContainerRef.current) return;
        const container = dateScrollContainerRef.current;
        const scrollAmount = window.innerWidth * 0.7;
        container.scrollTo({
            left: container.scrollLeft - scrollAmount,
            behavior: 'smooth',
        });
    }



    return (
        <>
            <TopProgressBar percentageFilled={80} displayString={language === Language.GERMAN ? (cmsConfig?.progressDisplayDe ?? 'SCHRITT 4 / 5') : (cmsConfig?.progressDisplayEn ?? 'STEP 4 / 5')} onClickBackButton={() => navigate("/services")} />
            <div className='flex flex-col gap-2 min-h-screen px-4 pt-2 pb-64'>
                <FixedBottomBar />

                <div className='w-full flex flex-row items-center gap-4 px-6 py-4'>
                    <img
                        src={selectedStore?.images?.[0] ?? ''}
                        alt={selectedStore?.name}
                        className="w-16 h-16 rounded-[8px] object-cover"
                    />
                    <div className='w-full flex flex-col gap-0'>
                        <span className='text-sm text-burgundy font-semibold'>Kalia Lab {selectedStore?.name}</span>
                        <span className='text-sm text-burgundy font-light'>{selectedStore?.address}, {selectedStore?.zipCode}</span>
                    </div>
                </div>

                {showPastPractitionerPicker && (
                    <PastPractitionerPickerRow
                        practitioners={pastPractitioners}
                        selectedPractitionerId={preferredPractitionerId}
                        onChange={handlePractitionerChange}
                    />
                )}

                {/* Date Selector Row */}
                <div className="relative w-full bg-white p-4 rounded-[5px] flex flex-col gap-1 drop-shadow-md">
                    <div className='w-full flex flex-row items-center justify-between'>
                        <span className='text-lg font-light text-burgundy'>
                            {language === Language.GERMAN ? (cmsConfig?.pageTitleDe ?? 'Wähle deinen Termin') : (cmsConfig?.pageTitleEn ?? 'Select your appointment')}
                        </span>
                        <div className='flex flex-row items-center bg-burgundy/10 rounded-[5px] p-1'>
                            <button
                                className={`p-2 rounded-[5px] ${timeslotViewMode === 'day-view' ? 'bg-white' : ''}`}
                                onClick={() => setTimeslotViewMode('day-view')}
                            >
                                <Eye className="w-4 h-4 text-burgundy" />
                            </button>
                            <button
                                className={`p-2 rounded-[5px] ${timeslotViewMode === 'month-view' ? 'bg-white' : ''}`}
                                onClick={() => setTimeslotViewMode('month-view')}
                            >
                                <Calendar className="w-4 h-4 text-burgundy" />
                            </button>
                        </div>
                    </div>

                    <Separator className='my-2' />

                    <div className='relative overflow-x-auto' ref={dateScrollContainerRef}>
                        {availabilitiesQuery.isFetching ? (
                            timeslotViewMode === 'day-view' ? (
                                <div className='flex gap-3 pb-4' style={{ minWidth: 'max-content' }}>
                                    {[...Array(7)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="flex flex-col items-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 animate-pulse"
                                        >
                                            <div className="w-8 h-5 bg-burgundy/20 rounded" />
                                            <div className="w-10 h-3 bg-burgundy/20 rounded mt-1" />
                                            <div className="w-2 h-2 rounded-full bg-burgundy/20 mt-1" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className='flex flex-col gap-4 pb-4 overflow-x-scroll'>
                                    {/* Month header with navigation */}
                                    <div className='flex flex-row items-center justify-between'>
                                        <div className="w-32 h-5 bg-burgundy/20 rounded animate-pulse" />
                                        <div className='flex flex-row items-center gap-2'>
                                            <div className="w-8 h-8 rounded-full bg-burgundy/20 animate-pulse" />
                                            <div className="w-8 h-8 rounded-full bg-burgundy/20 animate-pulse" />
                                        </div>
                                    </div>

                                    {/* Weekday headers */}
                                    <div className='grid grid-cols-7 gap-0.5'>
                                        {(language === Language.GERMAN
                                            ? ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']
                                            : ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
                                        ).map((day) => (
                                            <div key={day} className='flex items-center justify-center py-2'>
                                                <span className='text-xs font-semibold text-burgundy'>{day}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Calendar days grid skeleton */}
                                    <div className='grid grid-cols-7 gap-0.5'>
                                        {[...Array(35)].map((_, i) => (
                                            <div key={i} className='flex flex-col items-center justify-center py-2 animate-pulse'>
                                                <div className="w-6 h-4 bg-burgundy/20 rounded" />
                                                <div className="w-1.5 h-1.5 rounded-full bg-burgundy/20 mt-1" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )
                        ) : timeslotViewMode === 'day-view' ? (
                            <div className='flex gap-3 pb-4 w-max'>
                                {isAfter(startOfMonth(currentMonth), startOfMonth(new Date())) && (
                                    <button
                                        onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                        className="flex flex-col items-center justify-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 hover:border-burgundy/50"
                                    >
                                        <ChevronLeftIcon className="h-5 w-5 text-burgundy" />
                                    </button>
                                )}
                                {monthDays.map((dayStr, index) => {
                                    const dayLocal = new LocalDateTime(`${dayStr} 10:00`, 'Europe/Berlin'); // 10:00 is random. Just set so that it's comfortably a time within the date.

                                    const appointmentsCount = getAppointmentsCount(dayLocal);
                                    const isSelected = selectedDate && isSameDay(selectedDate.toUTC(), dayLocal.toUTC());
                                    const isSunday = isSundayDate(dayStr);
                                    const isLastDayOfMonth = index === monthDays.length - 1;
                                    const showWeekDivider = isSunday && !isLastDayOfMonth;
                                    const isClosed = isStoreClosedOnDate(dayStr, selectedStore?.federalState, appointmentsCount > 0);

                                    return (
                                        <div key={dayStr} className="flex items-center gap-3">
                                            <button
                                                data-date={dayStr}
                                                onClick={() => !isClosed && setSelectedDate(dayLocal)}
                                                disabled={isClosed}
                                                className={`flex flex-col items-center gap-1 p-2 rounded-[5px] min-w-[40px] relative ${isSelected
                                                    ? 'border-2 border-burgundy text-black font-medium pb-4'
                                                    : isClosed
                                                        ? 'border border-burgundy/30 opacity-60 cursor-not-allowed'
                                                        : 'border border-burgundy/30 hover:border-burgundy/50'
                                                    }`}
                                            >
                                                <span className={`text-xs text-burgundy ${isSelected ? 'font-medium' : 'font-light'}`}>
                                                    {dayLocal.format("EEEEEE", { locale: getDateLocaleByLocale(language) })}
                                                </span>
                                                <span className={`text-[13px] text-burgundy ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                                    {dayLocal.format("d MMM", { locale: getDateLocaleByLocale(language) })}
                                                </span>
                                                {appointmentsCount > 0 ? (
                                                    <div className='w-2 h-2 rounded-full bg-[#20A26C] mt-1' />
                                                ) : (
                                                    <div className='w-2 h-2 rounded-full bg-light-gray mt-1' />
                                                )}
                                            </button>
                                            {showWeekDivider && (
                                                <div className='h-8 w-px bg-burgundy/20' />
                                            )}
                                        </div>
                                    );
                                })}
                                <button
                                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                    className="flex flex-col items-center justify-center gap-1 p-3 rounded-[5px] min-w-[40px] border border-burgundy/30 hover:border-burgundy/50"
                                >
                                    <ChevronRightIcon className="h-5 w-5 text-burgundy" />
                                </button>
                            </div>
                        ) : (
                            <div className='flex flex-col gap-2'>
                                {/* Month header with navigation */}
                                <div className='flex flex-row items-center justify-between'>
                                    <span className='text-base font-medium text-burgundy'>
                                        {format(currentMonth, "MMMM yyyy", { locale: getDateLocaleByLocale(language) })}
                                    </span>
                                    <div className='flex flex-row items-center gap-2'>
                                        <button
                                            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                            disabled={!canGoPrevious}
                                            className={`w-8 h-8 rounded-full border border-burgundy/30 flex items-center justify-center ${!canGoPrevious ? 'opacity-40 cursor-not-allowed' : 'hover:border-burgundy/50'}`}
                                        >
                                            <ChevronLeftIcon className="h-4 w-4 text-burgundy" />
                                        </button>
                                        <button
                                            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                            className="w-8 h-8 rounded-full border border-burgundy/30 flex items-center justify-center hover:border-burgundy/50"
                                        >
                                            <ChevronRightIcon className="h-4 w-4 text-burgundy" />
                                        </button>
                                    </div>
                                </div>

                                {/* Weekday headers */}
                                <div className='grid grid-cols-7 gap-0.5'>
                                    {(language === Language.GERMAN
                                        ? ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO']
                                        : ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']
                                    ).map((day) => (
                                        <div key={day} className='flex items-center justify-center py-1'>
                                            <span className='text-xs font-semibold text-burgundy'>{day}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Calendar days grid */}
                                <div className='grid grid-cols-7 justify-items-center'>
                                    {(() => {
                                        const monthStart = startOfMonth(currentMonth);
                                        const monthEnd = endOfMonth(currentMonth);
                                        const today = new Date();

                                        // Get the day of week for the first day (0 = Sunday, 1 = Monday, etc.)
                                        // We want Monday = 0, so adjust
                                        let startDayOfWeek = monthStart.getDay() - 1;
                                        if (startDayOfWeek < 0) startDayOfWeek = 6; // Sunday becomes 6

                                        const daysInMonth = monthEnd.getDate();
                                        const cells = [];

                                        // Add empty cells for days before the first of the month
                                        for (let i = 0; i < startDayOfWeek; i++) {
                                            cells.push(
                                                <div key={`empty-${i}`} className='flex items-center justify-center py-2' />
                                            );
                                        }

                                        // Add cells for each day of the month
                                        for (let day = 1; day <= daysInMonth; day++) {
                                            const dayStr = format(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day), 'yyyy-MM-dd');
                                            const dayLocal = new LocalDateTime(`${dayStr} 10:00`, 'Europe/Berlin');
                                            const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

                                            const isPast = isBefore(dayDate, startOfDay(today));
                                            const appointmentsCount = getAppointmentsCount(dayLocal);
                                            const isDisabled = isPast || isStoreClosedOnDate(dayStr, selectedStore?.federalState, appointmentsCount > 0);
                                            const isSelected = selectedDate && isSameDay(selectedDate.toUTC(), dayLocal.toUTC());

                                            cells.push(
                                                <button
                                                    key={dayStr}
                                                    data-date={dayStr}
                                                    onClick={() => !isDisabled && setSelectedDate(dayLocal)}
                                                    disabled={isDisabled}
                                                    className={`flex flex-col items-center justify-center px-3 py-2 rounded-[6px] border ${isDisabled ? 'opacity-60 cursor-not-allowed' : ''} ${isSelected ? 'border-burgundy' : 'border-transparent'}`}
                                                >
                                                    <span className={`relative text-sm text-burgundy ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                                        {day}
                                                        {appointmentsCount === 0 && !isDisabled && (
                                                            <span className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-px bg-burgundy" />
                                                        )}
                                                    </span>
                                                    {!isDisabled && (
                                                        appointmentsCount > 0 ? (
                                                            <div className='w-1.5 h-1.5 rounded-full bg-[#20A26C] mt-0.5' />
                                                        ) : (
                                                            <div className='w-1.5 h-1.5 rounded-full bg-light-gray mt-0.5' />
                                                        )
                                                    )}
                                                </button>
                                            );
                                        }

                                        return cells;
                                    })()}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Available Appointments Section */}
                {selectedDate && (
                    <div className='flex flex-col gap-4 bg-white mt-2 p-4 drop-shadow-md rounded-[5px]'>
                        <span className='text-base font-light text-burgundy'>
                            {language === Language.GERMAN ? (cmsConfig?.availableAppointmentsLabelDe ?? 'Verfügbare Termine') : (cmsConfig?.availableAppointmentsLabelEn ?? 'Available appointments')}
                        </span>


                        {/* Time of Day Selector */}
                        <div className='grid grid-cols-3 gap-3'>
                            {timeOfDayOptions.map((option) => {
                                const Icon = option.icon;
                                const isSelected = selectedTimeOfDay === option.id;
                                const hasTimeslots = timeslotsByDate[selectedDate.getDisplayDate()]?.some(slot => {
                                    const hour = slot.timeslot.startTime.getHours();
                                    switch (option.id) {
                                        case 'morning':
                                            return hour < 12;
                                        case 'afternoon':
                                            return hour >= 12 && hour < 16;
                                        case 'evening':
                                            return hour >= 16;
                                        default:
                                            return false;
                                    }
                                }) || false;

                                // if (!hasTimeslots) return null;

                                const totalCartPrice = cartItems.reduce((acc, item) => acc + getCartItemTotalPrice(item), 0);

                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => setSelectedTimeOfDay(option.id)}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-[5px] text-burgundy ${isSelected
                                            ? 'border-2 border-burgundy font-medium scale-110 md:scale-100'
                                            : 'border border-burgundy/50'
                                            }`}
                                    >
                                        <Icon className="h-4 w-4" />
                                        <span className='text-sm'>{option.range} {t('oClock')}</span>
                                        {hasTimeslots ? (
                                            <div className='w-2 h-2 rounded-full bg-[#20A26C]' />
                                        ) : (
                                            <span className='text-xs text-burgundy/60'>
                                                {language === Language.GERMAN 
                                                    ? (cmsConfig?.waitlistLabelDe ?? 'Warteliste') 
                                                    : (cmsConfig?.waitlistLabelEn ?? 'Waitlist')}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <Separator />

                        {getAppointmentsCount(selectedDate, selectedTimeOfDay) > 0 ? (
                            <>
                                {/* Timeslot Selector */}
                                {filteredTimeslots.length > 0 && (
                                    <div className='grid grid-cols-3 min-[350px]:grid-cols-4 gap-4'>
                                        {displayedTimeslots.map((timeslot, index) => {
                                            const isSelected = !selectedTimeslot ? false : selectedTimeslot.equals(timeslot.timeslot.startTime);

                                            return (
                                                <button
                                                    key={`${timeslot.timeslot.startTime.getDisplayDateTime()}-${index}`}
                                                    onClick={() => setSelectedTimeslot(timeslot.timeslot.startTime)}
                                                    className={`w-full flex justify-center items-center px-2 py-2 rounded-[5px] bg-white ${isSelected
                                                        ? 'border-2 border-burgundy text-burgundy font-medium'
                                                        : 'border border-burgundy/50 text-burgundy/70 font-normal'
                                                        }`}
                                                >
                                                    <span className='text-sm font-medium'>
                                                        {timeslot.timeslot.startTimeStr}
                                                    </span>
                                                </button>
                                            );
                                        })}

                                        {/* Show More Button */}
                                        {hasMoreTimeslots && !showMoreTimeslots && (
                                            <button
                                                onClick={() => setShowMoreTimeslots(true)}
                                                className="col-span-2 w-full items-center p-3 text-burgundy rounded-lg transition-colors duration-200 underline text-left"
                                            >
                                                {language === Language.GERMAN ? (cmsConfig?.showMoreTextDe ?? 'Mehr anzeigen') : (cmsConfig?.showMoreTextEn ?? 'Show more')}
                                            </button>
                                        )}

                                        {/* Show Less Button */}
                                        {showMoreTimeslots && hasMoreTimeslots && (
                                            <button
                                                onClick={() => setShowMoreTimeslots(false)}
                                                className="col-span-2 w-full items-center p-3 text-burgundy rounded-lg transition-colors duration-200 underline text-left"
                                            >
                                                {language === Language.GERMAN ? (cmsConfig?.showLessTextDe ?? 'Weniger anzeigen') : (cmsConfig?.showLessTextEn ?? 'Show less')}
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : (
                            /* Waitlist Option */
                            <form onSubmit={(e) => e.preventDefault()} className='flex flex-col gap-4 px-2'>
                                <span className='text-burgundy font-light leading-normal text-sm'>
                                    {language === Language.GERMAN ? (cmsConfig?.waitlistDescriptionDe ?? 'Wir benachrichtigen dich, sobald ein Termin frei wird.') : (cmsConfig?.waitlistDescriptionEn ?? "We'll notify you when an appointment becomes available.")}
                                </span>
                                <div className="flex flex-col gap-2">
                                    <Input
                                        id="waitlist-email"
                                        type="email"
                                        placeholder={language === Language.GERMAN ? (cmsConfig?.waitlistEmailPlaceholderDe ?? 'Deine E-Mail Adresse') : (cmsConfig?.waitlistEmailPlaceholderEn ?? 'Your e-mail address')}
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setWaitlistEmailError('');
                                        }}
                                        aria-invalid={!!waitlistEmailError}
                                        className={`m-0 p-0 shadow-none h-10 border-0 border-b rounded-none bg-transparent font-light text-burgundy placeholder:text-burgundy/50 focus-visible:ring-0 ${waitlistEmailError ? 'border-red-400 focus-visible:border-red-400' : 'border-burgundy focus-visible:border-burgundy'}`}
                                    />
                                    {waitlistEmailError ? (
                                        <span className="text-sm text-red-600">{waitlistEmailError}</span>
                                    ) : null}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleJoinWaitlist}
                                    disabled={!isWaitlistEmailValid || signUpForWaitlistMutation.isPending}
                                    className="w-full bg-white text-burgundy/80 rounded-[5px] py-2 text-center border border-burgundy/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {signUpForWaitlistMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin text-burgundy" aria-hidden />
                                    ) : null}
                                    {language === Language.GERMAN ? (cmsConfig?.waitlistButtonTextDe ?? 'Auf Warteliste setzen') : (cmsConfig?.waitlistButtonTextEn ?? 'Join waitlist')}
                                </button>
                            </form>
                        )}
                    </div>
                )}
                <div className='w-full flex flex-row-reverse mt-4'>
                    <button
                        onClick={() => navigate("/services")}
                        className='flex flex-row items-center gap-1'
                    >
                        <Plus className="w-5 h-5 text-burgundy" />
                        <span className="text-burgundy text-sm font-light">
                            {language === Language.GERMAN ? (cmsConfig?.addAnotherTreatmentTextDe ?? 'Weitere Behandlung hinzufügen') : (cmsConfig?.addAnotherTreatmentTextEn ?? 'Add another treatment')}
                        </span>
                    </button>
                </div>
            </div>
        </>
    );
};

export default CalendarSelection;